import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Redirect, Switch, useHistory, useLocation } from "react-router-dom";
import PlateApi from "./api/PlateApi";
import SuiteApi from "./api/SuiteApi";
import ToolsApi from "./api/ToolsApi";
import "./App.css";
import NF from "./components/404";
import Modal from "./components/Common/Modal";
import { PermissionProvider } from "./components/Common/PermissionProvider";
import { fetchImagerCapabilities } from "./components/FetchImagerCapabilities";
import { fetchImagingSchedule } from "./components/FetchImagingSchedules";
import { fetchModules } from "./components/FetchModules";
import { fetchModuleCapabilities } from "./components/FetchModulesCapabilities";
import { fetchModuleStates } from "./components/FetchModuleStates";
import { fetchPlateAddresses } from "./components/FetchPlateAddresses";
import { fetchStorageConfigs } from "./components/FetchStorageConfigs";
import { fetchTemperatureSensors } from "./components/FetchTemperatureSensors";
import DropLocationView from "./components/Imager/DropLocationView";
import Efi from "./components/Imager/Efi";
import FocusView from "./components/Imager/FocusView";
import Imager from "./components/Imager/Imager";
import LastViewImage from "./components/Imager/LastViewImage";
import Layout from "./components/Layout";
import Overview from "./components/Overview/Overview";
import LoadPort from "./components/Plate/LoadPort";
import Search from "./components/Plate/Search";
import PlateTypeEditor from "./components/PlateTypeEditor/PlateTypeEditor";
import PlateTypeEditorFloating from "./components/PlateTypeEditor/PlateTypeEditorFloating";
import PrivateRoute, {
  PrivateRouteWithPermission,
} from "./components/PrivateRoute";
import RedirectWithPermission from "./components/RedirectWithPermission";
import EventLogReport from "./components/Report/EventLogReport";
import Scheduling from "./components/Scheduling/Scheduling";
import ImagingHistory from "./components/Scheduling/ImagingHistory";
import SettingsImager from "./components/Settings/Imager/SettingsImager";
import SettingsRaspiCamera from "./components/Settings/PsrCamera/SettingsRaspiCamera";
import SettingsStorage from "./components/Settings/Storage/SettingsStorage";
import VectorEditor from "./components/Settings/VectorEditor/VectorEditor";
import SimulationView from "./components/Simulation/SimulationView";
import Licensing from "./components/Support/Licensing";
import UserManagement from "./components/Support/UserManagement";
import Temperature from "./components/Temperature/Temperature";
import DebugPage from "./components/Tools/DebugPage/DebugPage";
import Email from "./components/Tools/Email/Email";
import GeneralAndDiagnostic from "./components/Tools/General/GeneralAndDiagnostic";
import StoreAndRetrieve from "./components/Tools/StoreAndRetrieve/StoreAndRetrieve";
import UserActivityWatcher from "./components/UserActivityWatcher";
import ErrorView from "./ErrorView";
import { EModuleType } from "./Model/EModuleType";
import { Module } from "./Model/Module";
import {
  EFaultedReason,
  ESuiteMode,
  EChillerState,
  EChillerControlStatus,
  EPsrRetryState,
} from "./Model/ModuleState";
import { EComponentId } from "./Model/Permission";
import {
  EBarcodeScannerType,
  ECarrierAutoCalibrationType,
} from "./Model/PsrCapabilites";
import { onChangeUserCredential } from "./redux/actions/LocalUserActions";
import { getImagerCapabilitiesFetchStatus } from "./redux/reducers/ImagerCapabilitiesReducer";
import {
  getIsModuleHardwareDisconnected,
  getModuleCapabilitiesFetchStatus,
  getModuleCapability,
} from "./redux/reducers/ModuleCapabilitiesReducer";
import {
  getModuleFetchStatus,
  getModules,
} from "./redux/reducers/ModuleReducer";
import {
  getModuleState,
  getModuleStatesFetchStatus,
} from "./redux/reducers/ModuleStateReducer";
import { getPlateAddressesFetchStatus } from "./redux/reducers/PlateAddressReducer";
import { useTypedSelector } from "./redux/reducers/RootReducer";
import { getStorageConfigFetchStatus } from "./redux/reducers/StorageConfigReducer";
import {
  getTemperatureSensorsCollection,
  getTemperatureSensorsFetchStatus,
} from "./redux/reducers/TemperatureReducer";
import Wait from "./Wait";
import WarningView from "./WarningView";
import WarningWaitView from "./WarningWaitView";
import SystemBackup from "./components/Settings/SystemBackup/SystemBackup";
import Preferences from "./components/Settings/Preference/SettingsPreferenceView";
import Testing from "./components/Tools/Testing/Testing";
import { ENotificationType } from "./Model/ENotificationType";
import { sendNotification } from "./redux/actions/NotificationAction";
import SvgWarning from "./Icons/ModalNotification/Warning";
import MultiplePlateRetrievalFailureModal from "./components/Common/MultiplePlateRetrievalFailureModal/MultiplePlateRetrievalFailureModal";
import BannerView from "./components/Banner/BannerView";
import PsrStatus from "./components/Overview/PsrStatus";

function Main() {
  const dispatch = useDispatch();
  const history = useHistory();
  const fetchModuleStatus = useTypedSelector((state) =>
    getModuleFetchStatus(state)
  );
  const fetchModuleCapabilitiesStatus = useTypedSelector((state) =>
    getModuleCapabilitiesFetchStatus(state)
  );
  const fetchPlateAddressesStatus = useTypedSelector((state) =>
    getPlateAddressesFetchStatus(state)
  );
  const fetchModuleStatesStatus = useTypedSelector((state) =>
    getModuleStatesFetchStatus(state)
  );
  const fetchImagerCapabilitiesStatus = useTypedSelector((state) =>
    getImagerCapabilitiesFetchStatus(state)
  );
  const fetchTemperatureStatus = useTypedSelector((state) =>
    getTemperatureSensorsFetchStatus(state)
  );
  const fetchStorageConfigsStatus = useTypedSelector((state) =>
    getStorageConfigFetchStatus(state)
  );

  const imagerModules = useTypedSelector((state) =>
    getModules(state, EModuleType.IMAGER)
  );
  const temperatureModuleIdentifier = useTypedSelector(
    (state) => getTemperatureSensorsCollection(state).allIds[0]
  );
  const suiteState = useTypedSelector(
    (state) => state.moduleStateStore.moduleStateById["Suite"]?.suite
  );

  const suiteMode = suiteState?.suiteMode ?? ESuiteMode.Normal;
  const location = useLocation();

  const chillerModules = useTypedSelector((state) =>
    getModules(state, EModuleType.CHILLER)
  );
  const chillerModule =
    chillerModules !== undefined ? chillerModules[0] : ({} as Module);

  const chillerModuleState = useTypedSelector((state) =>
    getModuleState(state, chillerModule.identifier)
  );
  const chiller = chillerModuleState?.chiller !== undefined;

  const handleGetSoftwareVersion = () => {
    SuiteApi.getApplicationInfo().then((value) => {
      document.title = `ROCK IMAGER v${value.productVersion}`
    });
  }

  const isModuleHardwareDisconnected = useTypedSelector((state) => getIsModuleHardwareDisconnected(state));

  useEffect(() => {
    const chillerStatus = chillerModuleState?.chiller;
    if (chillerStatus != null && chillerStatus != undefined) {
      var chillerFailedInitialization: string[] = [];
      chillerStatus?.chillerControlState.forEach((controllerState) => {
        if (controllerState.isInitialConnectionSuccess == false)
          chillerFailedInitialization.push(controllerState.name);
      });

      if (chillerFailedInitialization.length > 0) {
        dispatch(
          sendNotification(
            ENotificationType.Warning,
            "Chiller " +
            chillerFailedInitialization.toString() +
            " is disconnected during initialization"
          )
        );
      } else if (
        chillerStatus?.state === EChillerState.Error ||
        chillerStatus?.state === EChillerState.Warning
      ) {
        var chillerSensorError: string[] = [];
        chillerStatus?.chillerControlState.forEach((controllerState) => {
          controllerState.sensorErrorList.forEach((sensorError) => {
            chillerSensorError.push(sensorError);
          });
          if (
            controllerState.connectionStatus ===
            EChillerControlStatus.Disconnected
          ) {
            chillerSensorError.push("Disconnected");
          }
          if (chillerSensorError.length > 0) {
            var notifType: ENotificationType =
              chillerStatus?.state === EChillerState.Error
                ? ENotificationType.Error
                : ENotificationType.Warning;
            dispatch(
              sendNotification(
                notifType,
                "Chiller " +
                controllerState.name +
                " error: " +
                chillerSensorError.toString()
              )
            );
          }
          chillerSensorError = [];
        });
      }
    }
  }, [chiller, chillerModuleState, dispatch]);

  const psrModules = useTypedSelector((state) =>
    getModules(state, EModuleType.PSR)
  );
  const psrModule = psrModules !== undefined ? psrModules[0] : ({} as Module);
  const psrIdentifier = psrModule ? psrModule.identifier : "";

  const psrState = useTypedSelector(
    (state) => getModuleState(state, psrIdentifier)?.psr
  );

  console.log("PsrState", psrState);

  const psrModuleCapability = useTypedSelector((state) =>
    getModuleCapability(state, psrIdentifier)
  );
  const [isSupportPsrCamera, setIsSupportedCamera] = useState(false);

  useEffect(() => {
    if (psrModule !== undefined && psrModuleCapability !== undefined) {
      const psrCapability = psrModuleCapability.psr;

      const isSupportPsrCamera =
        psrCapability !== undefined
          ? psrCapability.barcodeScanner.barcodeScannerType ===
          EBarcodeScannerType.PsrCamera ||
          psrCapability.carrierAutoCalibration?.carrierAutoCalibrationType ===
          ECarrierAutoCalibrationType.PsrCamera
          : false;

      setIsSupportedCamera(isSupportPsrCamera);
    }
  }, [psrModule, psrModuleCapability]);

  useEffect(() => {
    dispatch(fetchModules());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchModuleCapabilities());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchModuleStates());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchImagingSchedule());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchImagerCapabilities());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchTemperatureSensors());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchPlateAddresses());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchStorageConfigs());
  }, [dispatch]);

  useEffect(() => {
    handleGetSoftwareVersion()
    const onChangeLocalStorage = () => {
      dispatch(onChangeUserCredential());
    };

    window.addEventListener("storage", onChangeLocalStorage);
    return () => {
      window.removeEventListener("storage", onChangeLocalStorage);
    };
  }, [dispatch]);

  if (!fetchModuleStatus.completed) {
    return <Wait message="Loading Module Info" />;
  }

  if (fetchModuleStatus.error) {
    return <Wait message={fetchModuleStatus.error} />;
  }

  if (!fetchModuleCapabilitiesStatus.completed) {
    return <Wait message="Loading Module Capabilities Info" />;
  }

  if (fetchModuleCapabilitiesStatus.error) {
    return <Wait message={fetchModuleCapabilitiesStatus.error} />;
  }

  if (!fetchModuleStatesStatus.completed) {
    return <Wait message="Loading Module States" />;
  }

  if (fetchModuleStatesStatus.error) {
    return <Wait message={fetchModuleStatesStatus.error} />;
  }

  if (!fetchPlateAddressesStatus.completed) {
    return <Wait message="Loading Plate Addresses" />;
  }

  if (fetchPlateAddressesStatus.error) {
    return <Wait message={fetchPlateAddressesStatus.error} />;
  }

  if (!fetchImagerCapabilitiesStatus.completed) {
    return <Wait message="Loading Module Capabilities" />;
  }

  if (fetchImagerCapabilitiesStatus.error) {
    return <Wait message={fetchImagerCapabilitiesStatus.error} />;
  }

  if (!fetchTemperatureStatus.completed) {
    return <Wait message="Loading Temperature Sensors" />;
  }

  // if (fetchTemperatureStatus.error) {
  //   return <Wait message={fetchTemperatureStatus.error} />;
  // }

  if (!fetchStorageConfigsStatus.completed) {
    return <Wait message="Loading Storage Configs" />;
  }

  if (fetchStorageConfigsStatus.error) {
    return <Wait message={fetchStorageConfigsStatus.error} />;
  }

  if (suiteMode === ESuiteMode.Initializing) {
    const showInitializingMessage =
      (suiteState?.initializing?.messages?.length ?? 0) > 0;
    return (
      <div>
        <Wait message={"Initializing"} />;
        <Modal show={showInitializingMessage} hideOnBackdropClick={false}>
          <Modal.Body>
            <WarningView
              title="Initializing"
              message={
                (suiteState?.initializing?.messages?.length ?? 0) > 0
                  ? suiteState?.initializing?.messages[0] ?? "Please wait..."
                  : "Please wait..."
              }
              onClickOk={() => SuiteApi.acceptInitializationMessage()}
            />
          </Modal.Body>
        </Modal>
      </div>
    );
  }

  const isHardwareDisconnected = isModuleHardwareDisconnected || !(suiteState?.isHardwareConnected??true) || fetchTemperatureStatus.error;

  if (isHardwareDisconnected) {
    return (
      <div>
        <Wait message={"Hardware disconnected"} />;
        <Modal
          show={!(suiteState?.isHardwareConnected ?? true)}
          hideOnBackdropClick={false}>
          <Modal.Body>
            <WarningView
              icon={<svg width="83" height="83" viewBox="0 0 83 83" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M76.0834 41.5001C76.0834 60.5999 60.5999 76.0834 41.5001 76.0834C22.4002 76.0834 6.91675 60.5999 6.91675 41.5001C6.91675 22.4002 22.4002 6.91675 41.5001 6.91675C60.5999 6.91675 76.0834 22.4002 76.0834 41.5001ZM27.6667 62.2501L41.5001 48.4167L55.3334 62.2501L62.2501 55.3334L48.4167 41.5001L62.2501 27.6667L55.3334 20.7501L41.5001 34.5834L27.6667 20.7501L20.7501 27.6667L34.5834 41.5001L20.7501 55.3334L27.6667 62.2501Z" fill="#DB0000" />
              </svg>}
              title="Hardware Connection"
              message={"Hardware connection timeout — unable to communicate with hardware."
              }
              instruction={"All ongoing imaging processes will stop immediately. Upcoming scheduled imaging will only resume once the connection is restored."
              }
            />
          </Modal.Body>
        </Modal>
      </div>
    );
  }

  if (
    suiteMode === ESuiteMode.Testing &&
    !window.location.pathname.includes("/tools/testing")
  ) {
    history.push(`/tools/testing/`);
  }

  if (
    suiteMode === ESuiteMode.AutoCalibrating &&
    !window.location.pathname.includes("/settings/storage")
  ) {
    history.push(`/settings/storage/`);
  }

  return (
    <PermissionProvider>
      <div>
        <div className="App">
          <Switch>
            <PrivateRoute exact path="/debug" component={DebugPage} />
            <PrivateRoute
              exact
              path="/plate-type-editor-floating"
              component={PlateTypeEditorFloating}
            />

            <PrivateRoute>
              <Layout>
                <Switch>
                  <PrivateRoute exact path="/" component={Overview} />
                  <PrivateRoute exact path="/plate">
                    <RedirectWithPermission
                      to="/plate/search"
                      componentId={EComponentId.PlateSearch}
                    />
                    <RedirectWithPermission
                      to="/plate/loadport"
                      componentId={EComponentId.PlateLoadPort}
                    />
                  </PrivateRoute>
                  <PrivateRouteWithPermission
                    exact
                    path="/plate/search"
                    component={Search}
                    componentId={EComponentId.PlateSearch}
                  />
                  <PrivateRouteWithPermission
                    exact
                    path="/plate/loadport"
                    component={LoadPort}
                    componentId={EComponentId.PlateLoadPort}
                  />
                  <PrivateRoute exact path="/imager">
                    <RedirectWithPermission
                      to={"/imager/last-captured/"}
                      componentId={EComponentId.ImagerLastCaptured}
                    />
                    <RedirectWithPermission
                      to={"/imager/focus/"}
                      componentId={EComponentId.ImagerFocusGraph}
                    />
                    <RedirectWithPermission
                      to={"/imager/drop-location/"}
                      componentId={EComponentId.ImagerDropLocation}
                    />
                    <RedirectWithPermission
                      to={"/imager/efi/"}
                      componentId={EComponentId.ImagerEfi}
                    />
                    <RedirectWithPermission
                      to={"/imager/live-image/"}
                      componentId={EComponentId.ImagerLiveImage}
                    />
                  </PrivateRoute>
                  <PrivateRoute exact path="/imager/live-image">
                    <Redirect
                      to={`/imager/live-image/${imagerModules[0].identifier}`}
                    />
                  </PrivateRoute>
                  <PrivateRouteWithPermission
                    exact
                    path="/imager/live-image/:imagerIdentifier"
                    component={Imager}
                    componentId={EComponentId.ImagerLiveImage}
                  />
                  <PrivateRoute exact path="/imager/efi">
                    <Redirect
                      to={`/imager/efi/${imagerModules[0].identifier}`}
                    />
                  </PrivateRoute>
                  <PrivateRouteWithPermission
                    exact
                    path="/imager/efi/:imagerIdentifier/"
                    component={Efi}
                    componentId={EComponentId.ImagerEfi}
                  />
                  <PrivateRouteWithPermission
                    exact
                    path="/imager/efi/:imagerIdentifier/:lightPath"
                    component={Efi}
                    componentId={EComponentId.ImagerEfi}
                  />
                  <PrivateRoute exact path="/imager/drop-location">
                    <Redirect
                      to={`/imager/drop-location/${imagerModules[0].identifier}`}
                    />
                  </PrivateRoute>
                  <PrivateRouteWithPermission
                    exact
                    path="/imager/drop-location/:imagerIdentifier/"
                    component={DropLocationView}
                    componentId={EComponentId.ImagerDropLocation}
                  />
                  <PrivateRouteWithPermission
                    exact
                    path="/imager/drop-location/:imagerIdentifier/:lightPath"
                    component={DropLocationView}
                    componentId={EComponentId.ImagerDropLocation}
                  />
                  <PrivateRoute exact path="/imager/focus">
                    <Redirect
                      to={`/imager/focus/${imagerModules[0].identifier}`}
                    />
                  </PrivateRoute>
                  <PrivateRouteWithPermission
                    exact
                    path="/imager/focus/:imagerIdentifier/"
                    component={FocusView}
                    componentId={EComponentId.ImagerFocusGraph}
                  />
                  <PrivateRouteWithPermission
                    exact
                    path="/imager/focus/:imagerIdentifier/:lightPath"
                    component={FocusView}
                    componentId={EComponentId.ImagerFocusGraph}
                  />
                  <PrivateRoute exact path="/imager/last-captured">
                    <Redirect
                      to={`/imager/last-captured/${imagerModules[0].identifier}`}
                    />
                  </PrivateRoute>
                  <PrivateRouteWithPermission
                    exact
                    path="/imager/last-captured/:imagerIdentifier/"
                    component={LastViewImage}
                    componentId={EComponentId.ImagerLastCaptured}
                  />
                  <PrivateRoute exact path="/plate-type-editor">
                    <RedirectWithPermission
                      to={"/plate-type-editor/image-mask"}
                      componentId={EComponentId.PlateSetupImageMask}
                    />
                    <RedirectWithPermission
                      to={"/plate-type-editor/config"}
                      componentId={EComponentId.PlateSetupDefImgSetting}
                    />
                    <RedirectWithPermission
                      to={"/plate-type-editor/calibration"}
                      componentId={EComponentId.PlateSetupPlateTypeCalibration}
                    />
                  </PrivateRoute>
                  <PrivateRoute
                    exact
                    path="/plate-type-editor/:subMenu"
                    component={PlateTypeEditor}
                  />

                  <PrivateRouteWithPermission
                    exact
                    path="/temperature/:moduleIdentifier"
                    component={Temperature}
                    componentId={EComponentId.Temperature}
                  />
                  <PrivateRoute exact path="/temperature">
                    <Redirect
                      to={`/temperature/${temperatureModuleIdentifier}`}
                    />
                  </PrivateRoute>
                  <PrivateRoute
                    exact
                    path="/schedule/history"
                    component={ImagingHistory}
                  />
                  <PrivateRoute exact path="/schedule">
                    <Redirect to="/schedule/queued" />
                  </PrivateRoute>
                  <PrivateRouteWithPermission
                    exact
                    path="/schedule/:subMenu"
                    component={Scheduling}
                    componentId={EComponentId.Schedule}
                  />

                  <PrivateRoute exact path="/report-support">
                    <RedirectWithPermission
                      to="/report-support/user-management"
                      componentId={EComponentId.ReportAndSupportUserManagement}
                    />
                    <RedirectWithPermission
                      to="/report-support/licensing"
                      componentId={EComponentId.ReportAndSupportLicensing}
                    />
                    <RedirectWithPermission
                      to="/report-support/event-log"
                      componentId={EComponentId.ReportAndSupportEventLog}
                    />
                  </PrivateRoute>
                  <PrivateRouteWithPermission
                    exact
                    path="/report-support/event-log"
                    component={EventLogReport}
                    componentId={EComponentId.ReportAndSupportEventLog}
                  />

                  <PrivateRouteWithPermission
                    exact
                    path="/report-support/licensing"
                    component={Licensing}
                    componentId={EComponentId.ReportAndSupportLicensing}
                  />
                  <PrivateRouteWithPermission
                    exact
                    path="/report-support/user-management"
                    component={UserManagement}
                    componentId={EComponentId.ReportAndSupportUserManagement}
                  />
                  <PrivateRoute exact path="/tools">
                    <RedirectWithPermission
                      to={"/tools/simulation"}
                      componentId={EComponentId.ToolsSimulation}
                    />
                    <RedirectWithPermission
                      to={"/tools/testing"}
                      componentId={EComponentId.ToolsTesting}
                    />
                    <RedirectWithPermission
                      to={"/tools/email"}
                      componentId={EComponentId.ToolsEmail}
                    />
                    <RedirectWithPermission
                      to={"/tools/general"}
                      componentId={EComponentId.ToolsGeneral}
                    />
                    <RedirectWithPermission
                      to={"/tools/store-and-retrieve"}
                      componentId={EComponentId.ToolsStoreAndRetrieve}
                    />
                  </PrivateRoute>
                  <PrivateRouteWithPermission
                    exact
                    path="/tools/store-and-retrieve"
                    component={StoreAndRetrieve}
                    componentId={EComponentId.ToolsStoreAndRetrieve}
                  />
                  <PrivateRouteWithPermission
                    exact
                    path="/tools/general"
                    component={GeneralAndDiagnostic}
                    componentId={EComponentId.ToolsGeneral}
                  />
                  <PrivateRouteWithPermission
                    exact
                    path="/tools/email"
                    component={Email}
                    componentId={EComponentId.ToolsEmail}
                  />
                  <PrivateRouteWithPermission
                    exact
                    path="/tools/testing"
                    component={Testing}
                    componentId={EComponentId.ToolsTesting}
                  />
                  <PrivateRouteWithPermission
                    exact
                    path="/tools/simulation"
                    component={SimulationView}
                    componentId={EComponentId.ToolsSimulation}
                  />

                  <PrivateRoute exact path="/settings">
                    <RedirectWithPermission
                      to={"/settings/preferences"}
                      componentId={EComponentId.SettingsPreferences}
                    />
                    <RedirectWithPermission
                      to={"/settings/system-backup"}
                      componentId={EComponentId.SettingsSystemBackup}
                    />
                    <RedirectWithPermission
                      to={"/settings/psr-camera"}
                      componentId={EComponentId.SettingsPsrCamera}
                    />
                    <RedirectWithPermission
                      to={"/settings/storage"}
                      componentId={EComponentId.SettingsStorage}
                    />
                    <RedirectWithPermission
                      to={"/settings/imager"}
                      componentId={EComponentId.SettingsImager}
                    />
                    <RedirectWithPermission
                      to={"/settings/vector-editor"}
                      componentId={EComponentId.SettingsVectorEditor}
                    />
                  </PrivateRoute>
                  <PrivateRouteWithPermission
                    exact
                    path="/settings/vector-editor"
                    component={VectorEditor}
                    componentId={EComponentId.SettingsVectorEditor}
                  />
                  {/* <PrivateRouteWithPermission
                    exact
                    path="/settings/hotel-four-point-calibration"
                    component={HotelFourPointView}
                    componentId={EComponentId.ServicingHotelCalibration}
                  /> */}
                  <PrivateRouteWithPermission
                    exact
                    path="/settings/imager/"
                    component={SettingsImager}
                    componentId={EComponentId.SettingsImager}
                  />
                  <PrivateRouteWithPermission
                    exact
                    path="/settings/storage/"
                    component={SettingsStorage}
                    componentId={EComponentId.SettingsStorage}
                  />
                  <PrivateRouteWithPermission
                    exact
                    path="/settings/psr-camera/"
                    component={SettingsRaspiCamera}
                    componentId={EComponentId.SettingsPsrCamera}
                    additionalRule={isSupportPsrCamera}
                  />
                  <PrivateRouteWithPermission
                    exact
                    path="/settings/system-backup/"
                    component={SystemBackup}
                    componentId={EComponentId.SettingsSystemBackup}
                  />
                  <PrivateRouteWithPermission
                    exact
                    path="/settings/preferences/"
                    component={Preferences}
                    componentId={EComponentId.SettingsPreferences}
                  />
                  <PrivateRoute component={NF} />
                </Switch>
              </Layout>
            </PrivateRoute>
          </Switch>
        </div>
        <Modal
          show={suiteMode === ESuiteMode.Homing}
          hideOnBackdropClick={false}
          style={{
            width: 686,
            minHeight: 422,
          }}
        >
          <Modal.Body>
            <WarningWaitView
              title="Homing"
              message={"Please wait,\n Homing in progress... "}
            />
          </Modal.Body>
        </Modal>

        <Modal
          show={suiteMode === ESuiteMode.Diagnosing}
          hideOnBackdropClick={false}
          style={{
            width: 686,
            minHeight: 422,
          }}
        >
          <Modal.Body>
            <WarningWaitView
              title="Diagnostic"
              message={"Diagnostics in progress... "}
            />
          </Modal.Body>
        </Modal>

        <Modal
          show={suiteMode === ESuiteMode.ReturningAbandonedPlate}
          hideOnBackdropClick={false}
          style={{
            width: 686,
            minHeight: 422,
          }}
        >
          <Modal.Body>
            <WarningWaitView
              title="Move Abandoned Plate"
              message={"Please wait,\n System returning abandoned plate..."}
            />
          </Modal.Body>
        </Modal>

        <Modal
          show={suiteMode === ESuiteMode.AbandonedPlate}
          hideOnBackdropClick={false}
        >
          <Modal.Body>
            <WarningView
              title="Move Abandoned Plate"
              message={
                suiteState?.abandonedPlate?.message ??
                "Cannot Return Abandonded Plate, no empty SBS one slot height on Load Port, ROCK IMAGER will attempt to return the plate."
              }
              instruction={
                suiteState?.abandonedPlate?.instruction ??
                "Please make sure there is an empty SBS one height slot in Load Port"
              }
              onClickOk={() => PlateApi.returnAbandonedPlate()}
            />
          </Modal.Body>
        </Modal>

        <Modal
          show={suiteMode === ESuiteMode.ArmCrashed}
          hideOnBackdropClick={false}
        >
          <Modal.Body>
            <WarningView
              title="Hand Crashed"
              message={
                suiteState?.crashed?.message ??
                "Hand crashed, ROCK IMAGER will attempt to home the system"
              }
              instruction={
                suiteState?.crashed?.instruction ??
                "Please remove any obstruction from the hand"
              }
              onClickOk={() => ToolsApi.home()}
            />
          </Modal.Body>
        </Modal>

        <Modal
          show={psrState?.retryState === EPsrRetryState.RetryRetrieve || psrState?.retryState === EPsrRetryState.RetryStore}
          hideOnBackdropClick={false}
          style={{
            position: "absolute",
            top: 20,
            backgroundColor: "transparent",
            minWidth: "50%"
          }}
        >
          <Modal.Body>
            <BannerView
              title={`The Psr is retying to ${psrState?.retryState === EPsrRetryState.RetryRetrieve ? "retrieve" : "store"} the plate this process maybe take some time to finish.`}
              message={"You can abort this process if you want by click abort button"}
              onClickAbort={() => PlateApi.stopRetryStoreAndRetrieve()}
            />
          </Modal.Body>
        </Modal>

        <Modal
          show={suiteMode === ESuiteMode.ReconnectingHardware}
          hideOnBackdropClick={false}
        >
          <Modal.Body>
            <WarningView
              title="Homing"
              message={
                <div style={{ textAlign: "center", paddingBottom: 40 }}>
                  <SvgWarning />
                  <h3>
                    PSR position is unsafe.
                  </h3>
                  <small>Click <b>Homing</b> to return it to a safe position.</small>
                </div>
              }
              okText="Homing"
              onClickOk={async () => await ToolsApi.home()}
            />
          </Modal.Body>
        </Modal>

        <Modal
          show={suiteMode === ESuiteMode.Error}
          hideOnBackdropClick={false}
        >
          <Modal.Body>
            {suiteState?.faulted?.reason ===
              EFaultedReason.MultiplePlateRetrievalFailure ||
              suiteState?.faulted?.reason ===
              EFaultedReason.MultiplePlateRetrievalFailureAcknowledged ? (
              <MultiplePlateRetrievalFailureModal
                title={
                  suiteState?.faulted?.title ??
                  (EFaultedReason.MultiplePlateRetrievalFailure
                    ? "Failed Retrieve Plate from Imager"
                    : "System Faulted")
                }
                message={
                  suiteState?.faulted?.message ?? (
                    <>
                      Contact{" "}
                      <a href="mailto:support@formulatrix.com">
                        support@formulatrix.com
                      </a>{" "}
                      for assistance
                    </>
                  )
                }
                detail={
                  suiteState?.faulted?.detail ??
                  (EFaultedReason.MultiplePlateRetrievalFailure
                    ? "Please remove plate from Imager manually"
                    : "System failed to retrieve plate from imager")
                }
                reason={
                  suiteState?.faulted?.reason ??
                  EFaultedReason.MultiplePlateRetrievalFailure
                }
              />
            ) : (
              <ErrorView
                title={suiteState?.faulted?.title ?? "Unhandled Exception."}
                message={
                  suiteState?.faulted?.message ??
                  "An internal error has occurred."
                }
                detail={
                  suiteState?.faulted?.detail ??
                  "An internal error has occurred."
                }
                reason={suiteState?.faulted?.reason ?? EFaultedReason.NoError}
              />
            )}
          </Modal.Body>
        </Modal>

        <UserActivityWatcher />
      </div>
    </PermissionProvider>
  );
}

export default Main;
